<template>
  <div class="form">
    <h1 class="form__title">{{ t('newPassword.title') }}</h1>
    <ui-input
        :type="'password'"
        :label="t('fields.newPassword')"
        :rules="[isPasswordValid]"
        v-model="password"
    />
    <ui-input
        :type="'password'"
        :label="t('fields.confirmNewPassword')"
        :rules="[compareStrings]"
        v-model="confirmPassword"
    />
    <div class="form__captcha">
      <Checkbox v-model="captcha" />
    </div>
    <ui-button
        class="form__button"
        :isDisabled="!isFormValid"
        @click="create"
    >
      {{ t('buttons.reset') }}
    </ui-button>
  </div>
</template>

<script setup>
import {useI18n} from "vue-i18n";
import UiInput from "@/components/ui/ui-input.vue";
import {isPasswordValid} from "@/core/rules/textFieldRules.js";
import {computed, ref} from "vue";
import UiButton from "@/components/ui/ui-button.vue";
import {ElNotification} from "element-plus";
import {Auth} from "@/core/api/auth.js";
import {useRoute, useRouter} from "vue-router";
import { Checkbox } from 'vue-recaptcha';

const {t} = useI18n()
const route = useRoute()
const router = useRouter()

const token = computed(() => {
  return route.query.token
})
const uid = computed(() => {
  return route.query.uid
})

const password = ref('')
const confirmPassword = ref('')
const captcha = ref()

const isLoading = ref(false)

const compareStrings = (v) => {
  if (v === password.value) return true
  return t('errors.compare')
}

const isFormValid = computed(() => {
  return (
    isPasswordValid(password.value) === true &&
    compareStrings(confirmPassword.value) === true &&
    captcha.value
  )
})

const create = async () => {
  isLoading.value = true

  const res = await Auth.confirmResetPassword(uid.value, token.value, password.value, confirmPassword.value)

  if (!res.errors) {
    await router.push({
      name: 'login',
      params: {
        locale: localStorage.getItem('ngs-locale') || 'en-gb'
      }
    })

    ElNotification({
      message: t('popup.successNewPassword'),
      type: 'success'
    })
  } else {
    ElNotification({
      title: t('popup.error'),
      message: res.errors[0].message,
      type: 'error'
    })
  }

  isLoading.value = false
}
</script>

<style lang="scss" scoped>
.form {
  width: 50%;
  margin-inline: auto;
  display: flex;
  flex-direction: column;
  gap: 15px;
  padding-block: 10px 10px;

  &__title {
    padding-bottom: 10px;
    border-bottom: 1px solid #E5E5E5;
    color: #5A5A5A;
    text-align: justify;
    font-size: 22px;
    font-weight: 600;
    line-height: 28px;
    letter-spacing: 0.44px;
  }

  &__link {
    transition: 0.2s;

    @include hover {
      color: #6c6c6c;
    }
  }

  &__button {
    padding-block: 12px;
    font-size: 14px;
  }

  &__bottom {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__lost {
    position: relative;
    color: #FF6162;
    font-size: 14px;
    font-weight: 400;
    line-height: 22.4px;

    &::after {
      content: "";
      position: absolute;
      bottom: -2px;
      left: 0;
      height: 2px;
      width: 0;
      transition: all .3s ease;
      background-color: #FF6162;
    }

    @include hover {
      &::after {
        width: 100%;
      }
    }
  }
}

@media (max-width: 1100px) {
  .form {
    width: 400px;
  }
}

@media (max-width: 500px) {
  .form {
    width: 100%;
  }
}
</style>